import { Vector2 } from "../../../common/vector2.js";
import { Game } from "../../../game.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ADVENTURE_REWARD_ICONS } from "../../../adventure/adventure_box_rewards.js";
import { rewardAmountText } from "../../../common/reward_format.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UICanvasRect } from "../../common_ui_elements/ui_canvas_rect.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import type { SurfaceAppearMotion, SurfaceCloseMotion } from "../../dom_backend/surface_motion.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";
import type { PrizeShotReward } from "../../../prize_shot/prize_shot_rules.js";
import { PRIZE_SHOT_COIN_ART, prizeShotCoinImageBounds } from "../../../prize_shot/prize_shot_coin_art.js";

export interface PrizeShotRewardsOverlayParams
{
  rewards: PrizeShotReward[];
  /** Grants the rewards; called once, on "Take". */
  on_claim: () => void;
  on_closed?: () => void;
}

/**
 * "Your reward" after a pack of Prize Shot balls, on the Figma frame 1080×2340.
 * Cards go in rows of MAX_PER_ROW; every extra row moves the logo up and the
 * button down, and the cards shrink only when the screen has no more room.
 */
export class PrizeShotRewardsOverlay extends AbstractOverlay
{
  private static readonly LOGO_Y = 560;
  private static readonly TITLE_Y = 875;
  private static readonly TITLE_H = 80;
  private static readonly MAX_PER_ROW = 4;
  private static readonly CARD_W = 188;
  private static readonly CARD_H = 232;
  private static readonly CARD_STEP_Y = 252;
  private static readonly BUTTON_CENTER_Y = 1560;
  private static readonly BUTTON_H = 258;
  private static readonly APPEAR_MS = 200;
  private static readonly CARD_STAGGER_MS = 70;
  private static readonly CLOSE_MS = 160;

  private overlay_params: PrizeShotRewardsOverlayParams;
  private backdrop!: UICanvasRect;
  private logo!: UIImage;
  private title!: UITextElement;
  private tiger: UIElement | null = null;
  /** The only part rebuilt when the overlay comes back from the pool. */
  private cards: UIElement[] = [];
  private claim_button!: UINineSliceElement;
  private rows = 1;
  private per_row = 1;
  private shift_up = 0;
  private shift_down = 0;
  private claimed = false;
  private close_notified = false;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, params: PrizeShotRewardsOverlayParams, overlay_id: OverlayId )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    this.overlay_params = params;

    this.computeLayout();
    this.createElements();
    this.playAppear();
  }

  /** The logo, title and button sit where the number of card rows puts them. */
  public static poolKeyFor( params: PrizeShotRewardsOverlayParams ): string
  {
    const count = params.rewards.length;
    return count === 0
      ? "PrizeShotRewardsOverlay:none"
      : `PrizeShotRewardsOverlay:rows${Math.ceil( count / PrizeShotRewardsOverlay.MAX_PER_ROW )}`;
  }

  public override isPoolable(): boolean
  {
    return true;
  }

  public override poolKey(): string
  {
    return PrizeShotRewardsOverlay.poolKeyFor( this.overlay_params );
  }

  public override onReuse( params: unknown ): void
  {
    const next = params as PrizeShotRewardsOverlayParams;
    if ( PrizeShotRewardsOverlay.poolKeyFor( next ) !== this.poolKey() )
      throw new Error( "PrizeShotRewardsOverlay: another layout" );

    this.overlay_params = next;
    this.params = next;
    this.claimed = false;
    this.close_notified = false;
    this.claim_button.setClickable( true );

    for ( const card of this.cards )
    {
      card.cleanup();
      this.removeChild( card );
    }
    this.cards = [];
    this.computeLayout();
    if ( this.hasRewards() )
      this.createCards();

    this.playAppear();
  }

  /** Everything fades or pops in; also replayed on every reuse. Cards bring their own. */
  private playAppear(): void
  {
    const now = performance.now();
    const statics: UIElement[] = [ this.backdrop, this.logo, this.title, this.claim_button ];
    if ( this.tiger )
      statics.push( this.tiger );
    for ( const element of statics )
    {
      element.stopAllTransforms();
      element.setOpacityImmediate( 1 );
    }

    this.backdrop.setOpacityImmediate( 0 );
    this.backdrop.fadeIn( PrizeShotRewardsOverlay.APPEAR_MS, now, Easing.EaseOutQuad );

    this.title.setOpacityImmediate( 0 );
    this.title.fadeIn( PrizeShotRewardsOverlay.APPEAR_MS, now, Easing.EaseOutQuad );

    if ( this.tiger )
      this.popIn( this.tiger, now + PrizeShotRewardsOverlay.APPEAR_MS * 0.5, 0.6, 1.06, 180, 200, 120 );

    const button_at = now + PrizeShotRewardsOverlay.APPEAR_MS + this.overlay_params.rewards.length * PrizeShotRewardsOverlay.CARD_STAGGER_MS;
    this.claim_button.setOpacityImmediate( 0 );
    this.claim_button.fadeIn( 180, button_at, Easing.EaseOutQuad );
  }

  private createElements(): void
  {
    this.createBackdrop();
    this.createLogo();
    this.createTitle();

    if ( this.hasRewards() )
      this.createCards();
    else
      this.createTiger();

    this.createButton();
  }

  public name(): string
  {
    return "PrizeShotRewardsOverlay";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }

  public override getViewportBackground(): string | null
  {
    return "transparent";
  }

  // The surface does not scale: the dim would grow with it. Its parts animate themselves.
  public override getAppearMotion(): SurfaceAppearMotion | null
  {
    return null;
  }

  public override getCloseMotion(): SurfaceCloseMotion | null
  {
    return null;
  }

  public override onOverlayWillClose(): void
  {
    if ( this.close_notified )
      return;

    this.close_notified = true;
    this.overlay_params.on_closed?.();
  }

  private hasRewards(): boolean
  {
    return this.overlay_params.rewards.length > 0;
  }

  /** Extra rows push the logo and the title up and the button down, half each, as far as the screen allows. */
  private computeLayout(): void
  {
    const count = this.overlay_params.rewards.length;
    this.rows = Math.max( 1, Math.ceil( count / PrizeShotRewardsOverlay.MAX_PER_ROW ) );
    this.per_row = Math.max( 1, Math.min( count, PrizeShotRewardsOverlay.MAX_PER_ROW ) );

    const extra = LayoutScaler.heightFromDesign( ( this.rows - 1 ) * PrizeShotRewardsOverlay.CARD_STEP_Y );
    const logo_y = this.fromCenter( PrizeShotRewardsOverlay.LOGO_Y );
    // The logo goes no higher than the HUD has it.
    const up_room = Math.max( 0, logo_y - LayoutScaler.yFromDesign( 135 ) );

    const button_bottom = this.fromCenter( PrizeShotRewardsOverlay.BUTTON_CENTER_Y ) + LayoutScaler.heightFromDesign( PrizeShotRewardsOverlay.BUTTON_H ) / 2;
    const down_room = Math.max( 0, this.rect.getH() - LayoutScaler.heightFromDesign( 140 ) - button_bottom );

    this.shift_down = Math.min( down_room, extra - Math.min( up_room, extra / 2 ) );
    this.shift_up = Math.min( up_room, extra - this.shift_down );
  }

  /** y of a design point relative to the middle of the frame. */
  private fromCenter( design_y: number ): number
  {
    return this.rect.getH2() + LayoutScaler.heightFromDesign( design_y - 1170 );
  }

  private createBackdrop(): void
  {
    this.backdrop = new UICanvasRect(
      this.ctx,
      -Game.safeAreaLeft(),
      -Game.safeAreaTop(),
      this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight(),
      this.rect.getH() + Game.safeAreaTop() + Game.safeAreaBottom(),
      "rgba(12, 4, 20, 0.72)",
    );
    this.backdrop.setOnClick( () => {} );
    this.addChild( this.backdrop );
  }

  private createLogo(): void
  {
    const design_logo_x = 313;
    const design_logo_w = 463;
    const design_logo_h = 297;

    this.logo = new UIImage(
      this.ctx,
      this.rect.getW2() + LayoutScaler.widthFromDesign( design_logo_x - 540 ),
      this.fromCenter( PrizeShotRewardsOverlay.LOGO_Y ) - this.shift_up,
      LayoutScaler.widthFromDesign( design_logo_w ),
      LayoutScaler.heightFromDesign( design_logo_h ),
      ImgID.UI_PrizeShotLogo
    );
    this.addChild( this.logo );
  }

  private createTitle(): void
  {
    const design_font_size = 60;

    this.title = new UITextElement(
      this.ctx,
      0,
      this.fromCenter( PrizeShotRewardsOverlay.TITLE_Y ) - this.shift_up,
      this.rect.getW(),
      LayoutScaler.heightFromDesign( PrizeShotRewardsOverlay.TITLE_H ),
      {
        localization_key: this.hasRewards() ? "PRIZE_SHOT.YOUR_REWARD" : "PRIZE_SHOT.NO_REWARD",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        shadow_blur: LayoutScaler.widthFromDesign( 8 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_x: 0,
        shadow_offset_y: LayoutScaler.yFromDesign( 4 ),
        auto_wrap: false,
        scale_to_width: true,
      }
    );
    this.addChild( this.title );
  }

  private createCards(): void
  {
    const gap = 40;
    const step_x = 209;
    const step_y = PrizeShotRewardsOverlay.CARD_STEP_Y;
    const rewards = this.overlay_params.rewards;

    const area_top = this.fromCenter( PrizeShotRewardsOverlay.TITLE_Y + PrizeShotRewardsOverlay.TITLE_H + gap ) - this.shift_up;
    // 107: half of the button body, without its frame and shadow.
    const area_bottom = this.fromCenter( PrizeShotRewardsOverlay.BUTTON_CENTER_Y - 107 - gap ) + this.shift_down;
    const block_h = LayoutScaler.heightFromDesign( ( this.rows - 1 ) * step_y + PrizeShotRewardsOverlay.CARD_H );
    const row_w = LayoutScaler.widthFromDesign( ( this.per_row - 1 ) * step_x + PrizeShotRewardsOverlay.CARD_W );
    const room_w = this.rect.getW() - 2 * LayoutScaler.widthFromDesign( 40 );
    const scale = Math.min( 1, ( area_bottom - area_top ) / Math.max( 1, block_h ), room_w / Math.max( 1, row_w ) );

    const card_h = this.scaled( PrizeShotRewardsOverlay.CARD_H, scale );
    const half_block = ( this.rows - 1 ) * this.scaled( step_y, scale ) / 2 + card_h / 2;
    const wanted_center_y = this.fromCenter( 1121 ) - this.shift_up;
    const block_center_y = Math.min( Math.max( wanted_center_y, area_top + half_block ), area_bottom - half_block );
    const first_center_y = block_center_y - ( this.rows - 1 ) * this.scaled( step_y, scale ) / 2;
    const now = performance.now();

    rewards.forEach( ( reward, index ) =>
    {
      const row = Math.floor( index / this.per_row );
      const col = index % this.per_row;
      const in_row = Math.min( this.per_row, rewards.length - row * this.per_row );
      const center_x = this.rect.getW2() + this.scaled( ( col - ( in_row - 1 ) / 2 ) * step_x, scale );
      const center_y = first_center_y + row * this.scaled( step_y, scale );
      const appear_at = now + PrizeShotRewardsOverlay.APPEAR_MS * 0.5 + index * PrizeShotRewardsOverlay.CARD_STAGGER_MS;

      this.createCard( reward, center_x, center_y, scale, appear_at );
    } );
  }

  private createCard( reward: PrizeShotReward, center_x: number, center_y: number, scale: number, appear_at: number ): void
  {
    const design_radius = 18;
    const design_icon_y = 30;
    const design_icon_size = 124;
    const design_text_y = 158;
    const design_text_h = 56;
    const design_font_size = 46;
    // The Prize Shot coin pile, not the single coin of the shared reward icons.
    const coins_aspect = PRIZE_SHOT_COIN_ART.width / PRIZE_SHOT_COIN_ART.height;

    const card_w = this.scaled( PrizeShotRewardsOverlay.CARD_W, scale );
    const card_h = this.scaled( PrizeShotRewardsOverlay.CARD_H, scale );
    const is_coin = reward.type === "coin";
    const icon_h = this.scaled( design_icon_size, scale );
    const icon_w = is_coin ? icon_h * coins_aspect : icon_h;
    const icon_bounds = is_coin
      ? prizeShotCoinImageBounds( ( card_w - icon_w ) / 2, this.scaled( design_icon_y, scale ), icon_w, icon_h )
      : { x: ( card_w - icon_w ) / 2, y: this.scaled( design_icon_y, scale ), width: icon_w, height: icon_h };

    const card = new UIElementHolder( this.ctx, center_x - card_w / 2, center_y - card_h / 2, card_w, card_h );

    const bg = new RoundedRectangle(
      this.ctx,
      0,
      0,
      card_w,
      card_h,
      this.scaled( design_radius, scale ),
      "rgba(8, 18, 40, 0.55)",
      undefined,
      false,
      "rgba(150, 196, 255, 0.55)",
      Math.max( 1, LayoutScaler.widthFromDesign( 3 ) )
    );
    card.addChild( bg );

    const icon = new UIImage(
      this.ctx,
      icon_bounds.x,
      icon_bounds.y,
      icon_bounds.width,
      icon_bounds.height,
      is_coin ? ImgID.UI_OnePlusOneOfferCoins : ADVENTURE_REWARD_ICONS[reward.type]
    );
    card.addChild( icon );

    const amount = new UITextElement(
      this.ctx,
      0,
      this.scaled( design_text_y, scale ),
      card_w,
      this.scaled( design_text_h, scale ),
      {
        text: is_coin ? String( reward.amount ) : rewardAmountText( reward.type, reward.amount ),
        font_size: this.scaled( design_font_size, scale ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
      }
    );
    card.addChild( amount );
    RewardTooltip.attachLegacy( card, reward.type );

    this.popIn( card, appear_at, 0, 1.12, 160, 170, 110 );
    // Under the button, as when the overlay is built: on reuse the button is already there.
    const button_index = this.claim_button ? this.getChildren().indexOf( this.claim_button ) : -1;
    if ( button_index >= 0 )
      this.addChildAtIndex( card, button_index );
    else
      this.addChild( card );
    this.cards.push( card );
  }

  /** Nothing found: the winking tiger lies on a plank where the cards would be. */
  private createTiger(): void
  {
    const design_center_y = 1195;
    const design_tiger_size = 420;
    const design_plank_w = 569;
    const design_plank_h = 49;
    const design_plank_dy = -46;

    const tiger_size = LayoutScaler.widthFromDesign( design_tiger_size );
    const plank_w = LayoutScaler.widthFromDesign( design_plank_w );
    const plank_h = LayoutScaler.heightFromDesign( design_plank_h );
    const center_y = this.fromCenter( design_center_y );

    const group = new UIElementHolder( this.ctx, this.rect.getW2() - plank_w / 2, center_y - tiger_size / 2, plank_w, tiger_size );

    // The plank goes first: the tiger's paws rest on top of it.
    const plank = new UIImage(
      this.ctx,
      0,
      tiger_size + LayoutScaler.heightFromDesign( design_plank_dy ) - plank_h / 2,
      plank_w,
      plank_h,
      ImgID.UI_PrizeShotTigerPlank
    );
    group.addChild( plank );

    const tiger = new UIImage(
      this.ctx,
      ( plank_w - tiger_size ) / 2,
      0,
      tiger_size,
      tiger_size,
      ImgID.UI_NoLevelsTiger
    );
    group.addChild( tiger );

    this.addChild( group );
    this.tiger = group;
  }

  private createButton(): void
  {
    const design_btn_w = 583;
    const design_text_w = 440;
    const design_text_dy = -3;
    const design_font_size = 80;
    const design_min_font_size = 44;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( PrizeShotRewardsOverlay.BUTTON_H );
    const text_w = LayoutScaler.widthFromDesign( design_text_w );

    this.claim_button = new UINineSliceElement(
      this.ctx,
      this.rect.getW2() - btn_w / 2,
      this.fromCenter( PrizeShotRewardsOverlay.BUTTON_CENTER_Y ) + this.shift_down - btn_h / 2,
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );

    const label = new UITextElement(
      this.ctx,
      ( btn_w - text_w ) / 2,
      LayoutScaler.heightFromDesign( design_text_dy ),
      text_w,
      btn_h,
      {
        localization_key: this.hasRewards() ? "CLAIM" : "OK",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        min_font_size: LayoutScaler.textSizeFromDesign( design_min_font_size ),
        font_weight: 800,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        shadow_color: "rgba(0, 0, 0, 0.43)",
        shadow_blur: LayoutScaler.widthFromDesign( 6 ),
        shadow_offset_x: 0,
        shadow_offset_y: LayoutScaler.heightFromDesign( 3 ),
      }
    );
    this.claim_button.addChild( label, true );
    this.claim_button.setOnClick( () => this.claim() );
    this.addChild( this.claim_button );
  }

  private claim(): void
  {
    if ( this.claimed )
      return;

    this.claimed = true;
    this.claim_button.setClickable( false );
    this.overlay_params.on_claim();

    if ( this.hasRewards() )
      void audioController.playSound( AudioClips.COINS_GROUP_FLY );

    const now = performance.now();
    const fading: UIElement[] = [ this.logo, this.title, ...this.cards, this.claim_button, this.backdrop ];
    if ( this.tiger )
      fading.push( this.tiger );
    for ( const element of fading )
      element.fadeOut( PrizeShotRewardsOverlay.CLOSE_MS, now, Easing.EaseInQuad );

    window.setTimeout( () =>
    {
      if ( OverlayManager.isOverlayOpen( this.getId() ) )
        OverlayManager.closeOverlay();
    }, PrizeShotRewardsOverlay.CLOSE_MS + 10 );
  }

  private scaled( design: number, scale: number ): number
  {
    return LayoutScaler.widthFromDesign( design * scale );
  }

  /** Fades in while it grows from `from` past `peak` and settles at full size. */
  private popIn( element: UIElement, at: number, from: number, peak: number, fade_ms: number, grow_ms: number, settle_ms: number ): void
  {
    const start = new Vector2( from, from );
    const top = new Vector2( peak, peak );

    element.setOpacityImmediate( 0 );
    element.setScaleImmediate( start );
    element.fadeIn( fade_ms, at, Easing.EaseOutQuad );
    element.addTransform( new TransformScale( element, true, start, top, at, grow_ms, { easing: Easing.EaseOutQuad } ) );
    element.addTransform( new TransformScale( element, true, top, new Vector2( 1, 1 ), at + grow_ms, settle_ms, { easing: Easing.EaseInQuad } ) );
  }
}
